import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PhoneInput } from '@/components/ui/phone-input';
import { parsePhone } from '@/lib/phone';
import { Smartphone } from '@/lib/icons';
import { useUssdSession } from '@/features/ussd-simulator/use-ussd-session';
import { Handset } from '@/features/ussd-simulator/components/Handset';
import { EnvelopeInspector } from '@/features/ussd-simulator/components/EnvelopeInspector';

function UssdSimulatorPage() {
  const session = useUssdSession();
  // Starts empty on purpose: the MSISDN acts as a lightweight "pin" on this
  // open page — the user must type the correct number to dial as that customer.
  const [msisdn, setMsisdn] = useState('');

  // The backend matches the MSISDN exactly, so dial only a complete number —
  // a partial one returns "customer not found" and reads like a backend fault.
  const dialTarget = parsePhone(msisdn).e164;

  const onDial = () => {
    if (!dialTarget || session.loading) return;
    // Start a fresh session for the entered MSISDN.
    session.reset();
    void session.dial(dialTarget);
  };

  const onReset = () => {
    if (session.loading) return;
    session.reset();
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="mx-auto max-w-6xl px-4 py-4">
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-primary" />
            <h1 className="text-xl font-bold tracking-tight">USSD Simulator</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            A Telesom handset emulator that POSTs the SDF envelope to the real
            backend USSD endpoint (<code className="font-mono">
              /bnplUssdSelfcareApp/web/ussd-menu
            </code>) and renders the returned screen. Enter a customer MSISDN,
            dial <span className="font-mono">*123#</span>, and walk the menu —
            every request/response envelope is shown on the right.
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6">
        <Card className="mb-6">
          <CardContent className="flex flex-col gap-3 pt-6 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-1.5">
              <label
                htmlFor="msisdn"
                className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
              >
                Customer MSISDN
              </label>
              <PhoneInput
                id="msisdn"
                value={msisdn}
                onChange={setMsisdn}
                onEnter={onDial}
              />
            </div>
            <Button
              type="button"
              onClick={onDial}
              disabled={session.loading || !dialTarget}
            >
              Dial *123#
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onReset}
              disabled={session.loading || session.exchanges.length === 0}
            >
              Reset
            </Button>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Handset</CardTitle>
              <CardDescription>
                Session <span className="font-mono">{session.sessionid}</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Handset session={session} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Envelope inspector</CardTitle>
              <CardDescription>
                Request / response timeline (newest last)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <EnvelopeInspector exchanges={session.exchanges} />
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

export const Route = createFileRoute('/ussd-simulator')({
  component: UssdSimulatorPage,
});
